/**
 * Canonical posts-table columns (ARCHITECTURE §3.1, §12.3).
 *
 * One entry per display column of `PostRowDTO`. The table (TanStack Table), the column-layout
 * UI state (`ui_state['columns:posts']`), the `sort` query param and CSV export all key off
 * these ids — there is no second list anywhere.
 *
 * Array order is the default column order; `defaultVisible` is the default visibility.
 */
export const POSTS_COLUMNS = [
    {
        id: 'author',
        label: 'Author',
        description: 'The profile that published the post.',
        kind: 'text',
        defaultVisible: true,
        sortable: true,
        groupOnly: true,
        align: 'left',
    },
    {
        id: 'preview',
        label: 'Post',
        description: 'First 200 characters of the post text.',
        kind: 'preview',
        defaultVisible: true,
        sortable: false,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'publishedAt',
        label: 'Published',
        description: 'When the post went live on LinkedIn, in the app timezone.',
        kind: 'date',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'postType',
        label: 'Post type',
        description: 'Original, repost or reshare with commentary. Only counted types feed metrics.',
        kind: 'enum',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'subtype',
        label: 'Subtype',
        description: 'Finer annotation the mapper recognised without promoting it to a post type: newsletter, link share, group post.',
        kind: 'text',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'contentType',
        label: 'Content type',
        description: 'The dominant medium of the post: text, image, video, document, poll or article.',
        kind: 'enum',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'reactions',
        label: 'Reactions',
        description: 'Reactions as of the most recent scrape of this post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'reactionsX',
        label: 'R ×',
        exportLabel: 'Reactions ×',
        description: "Reactions divided by the author's own median reactions.",
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'comments',
        label: 'Comments',
        description: 'Comments as of the most recent scrape of this post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'commentsX',
        label: 'C ×',
        exportLabel: 'Comments ×',
        description: "Comments divided by the author's own median comments.",
        kind: 'multiplier',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'shares',
        label: 'Shares',
        description: 'Shares as of the most recent scrape of this post.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'sharesX',
        label: 'S ×',
        exportLabel: 'Shares ×',
        description: "Shares divided by the author's own median shares.",
        kind: 'multiplier',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'saves',
        label: 'Saves',
        description: 'Saves as of the most recent scrape. Shows n/a when the scraper omits saves.',
        kind: 'metric',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'savesX',
        label: 'Sv ×',
        exportLabel: 'Saves ×',
        description: "Saves divided by the author's own median saves.",
        kind: 'multiplier',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'totalEngagement',
        label: 'Total eng.',
        exportLabel: 'Total engagement',
        description: 'Reactions plus comments plus shares plus saves.',
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'authorFollowers',
        label: 'Followers',
        exportLabel: 'Author followers',
        description: "The author's latest scraped follower count.",
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        groupOnly: true,
        align: 'left',
    },
    {
        id: 'engagementRate',
        label: 'Eng. rate',
        exportLabel: 'Engagement rate %',
        description: "Total engagement divided by the author's latest follower count, as a percent.",
        kind: 'metric',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'words',
        label: 'Words',
        description: 'Word count of the post text.',
        kind: 'number',
        defaultVisible: true,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'chars',
        label: 'Characters',
        description: 'Character count of the post text.',
        kind: 'number',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'hasMedia',
        label: 'Media',
        description: 'Whether the post carries any image, video or document attachment.',
        kind: 'boolean',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'edited',
        label: 'Edited',
        description: 'Whether LinkedIn marks the post as edited after publishing.',
        kind: 'boolean',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'mentions',
        label: 'Mentions',
        description: 'People and companies tagged in the post text.',
        kind: 'number',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'link',
        label: 'Link',
        exportLabel: 'Has external link',
        description: 'Whether the post text carries an outbound link.',
        kind: 'boolean',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'firstSeenAt',
        label: 'First seen',
        description: 'When this app first captured the post.',
        kind: 'date',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'lastUpdatedAt',
        label: 'Last updated',
        description: 'When any field of the post last changed.',
        kind: 'date',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'metricsAsOf',
        label: 'Metrics as of',
        description: 'The scrape the metrics in this row come from. Older posts stop being re-scraped.',
        kind: 'date',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'possiblyDeleted',
        label: 'Possibly deleted',
        description: 'The post stopped appearing in scrapes. Its stored data is kept either way.',
        kind: 'boolean',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
    {
        id: 'savedAt',
        label: 'Saved',
        exportLabel: 'Saved at',
        description: 'When you saved this post to your collection. Empty when it is not saved.',
        kind: 'date',
        defaultVisible: false,
        sortable: true,
        groupOnly: false,
        align: 'left',
    },
];
/** Default order — index in this array is the default position. */
export const POSTS_COLUMN_IDS = POSTS_COLUMNS.map((c) => c.id);
export const DEFAULT_VISIBLE_POSTS_COLUMNS = POSTS_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id);
export const GROUP_ONLY_POSTS_COLUMNS = POSTS_COLUMNS.filter((c) => c.groupOnly).map((c) => c.id);
/**
 * Sortable column ids, as a literal tuple so it can drive `z.enum`.
 * `satisfies` keeps it provably a subset of the column ids.
 */
export const POSTS_SORT_KEYS = [
    'author',
    'publishedAt',
    'postType',
    'subtype',
    'contentType',
    'reactions',
    'reactionsX',
    'comments',
    'commentsX',
    'shares',
    'sharesX',
    'saves',
    'savesX',
    'totalEngagement',
    'authorFollowers',
    'engagementRate',
    'words',
    'chars',
    'hasMedia',
    'edited',
    'mentions',
    'link',
    'firstSeenAt',
    'lastUpdatedAt',
    'metricsAsOf',
    'possiblyDeleted',
    'savedAt',
];
export const DEFAULT_POSTS_SORT = {
    key: 'publishedAt',
    dir: 'desc',
};
export function getPostsColumn(id) {
    const found = POSTS_COLUMNS.find((c) => c.id === id);
    if (!found)
        throw new Error(`unknown posts column: ${id}`);
    return found;
}
//# sourceMappingURL=columns.js.map